import { productKey, type PriceQuote } from './prices';
import type { SupplierRef } from './types';

/**
 * Prețurile „ca la bursă”: pe fiecare material, cel mai mic preț de la ultima
 * cerere, cine îl are și de la cine să comanzi.
 *
 * Unitatea de măsură e **cererea**, nu oferta: o cerere e o decizie, iar
 * furnizorii ei au ofertat pentru același lucru, în aceleași zile. De aceea
 * „acum” înseamnă minimul ultimei cereri (nu oferta sosită ultima, care e de
 * obicei a celui mai lent furnizor), iar variația compară minim cu minim, ca
 * să nu iasă diferența dintre doi furnizori drept „scumpire”.
 */

/** Perioada din pagina Prețuri: 6 luni, 12 luni sau tot istoricul. */
export type PricePeriod = '6' | '12' | 'tot';

/** Ce trebuie știut despre o cerere, în afară de prețuri. */
export interface RfqMeta {
  id: string;
  title: string;
  /** ISO: prima trimitere (`RfqSend.sentAt` cel mai vechi), altfel `createdAt` */
  at: string;
  /** nici închisă, nici anulată, furnizor încă neales */
  open: boolean;
  canceled: boolean;
  /** câte trimiteri au plecat (`sentAt` nenul) */
  invited: number;
  /**
   * câți furnizori au trimis oferta, chiar dacă n-au dat preț la toate
   * materialele; lipsă = se socotesc furnizorii cu preț la material
   */
  responded?: number;
}

export interface MarketPoint {
  rfqId: string;
  at: string;
  price: number;
  supplier: SupplierRef;
}

export interface MarketRank {
  supplier: SupplierRef;
  price: number;
  /** față de `now`: 0,016 = +1,6% */
  diff: number;
  best: boolean;
}

export interface MarketProduct {
  key: string;
  desc: string;
  unit: string;
  /** cererile din perioadă (cel puțin 1: ultima) */
  requests: number;
  last: {
    rfqId: string;
    rfqTitle: string;
    at: string;
    received: number;
    invited: number;
    complete: boolean;
  };
  /** cel mai mic preț la ultima cerere */
  now: number;
  /** furnizorul recomandat */
  pick: SupplierRef;
  /** ceilalți cu exact același preț ca `pick` */
  ties: SupplierRef[];
  next: { supplier: SupplierRef; price: number } | null;
  /** `now` față de minimul cererii anterioare; null la prima cerere */
  change: number | null;
  rank: MarketRank[];
  /** câte un punct pe cerere din perioadă, vechi → nou */
  points: MarketPoint[];
  lo: number;
  hi: number;
  /** câștigurile din perioadă, descrescător; doar furnizorii cu cel puțin unul */
  wins: { supplier: SupplierRef; count: number }[];
  /** false: nicio cerere în perioadă, se arată doar ultima (mai veche) */
  inPeriod: boolean;
}

export interface PriceMarket {
  products: MarketProduct[];
  /** id furnizor → culoarea 0..3; cine lipsește e gri */
  slots: Record<string, number>;
}

/**
 * Câte culori de furnizor: doar patru trec validatorul de daltonism pe toate
 * perechile, iar oricare doi furnizori pot sta în același clasament.
 */
export const MARKET_SLOTS = 4;
/** Banda de deasupra tabelului: mai mult de atât nu se mai citește. */
export const BAND_MAX = 20;

const byName = (a: SupplierRef, b: SupplierRef): number => a.name.localeCompare(b.name, 'ro');

/** Cel mai mic preț al fiecărui furnizor dintr-o cerere, cu ora ofertei. */
function perSupplier(quotes: PriceQuote[]): Map<string, PriceQuote> {
  const out = new Map<string, PriceQuote>();
  for (const q of quotes) {
    const was = out.get(q.supplier.id);
    if (!was || q.price < was.price || (q.price === was.price && q.at < was.at))
      out.set(q.supplier.id, q);
  }
  return out;
}

export function buildMarket(
  quotes: PriceQuote[],
  rfqs: RfqMeta[],
  from: string | null,
): PriceMarket {
  // anulată = nu s-a cumpărat nimic; prețurile ei n-au fost o decizie
  const meta = new Map(rfqs.filter((r) => !r.canceled).map((r) => [r.id, r]));
  // un preț 0 e „inclus” (transport, de pildă), nu un preț de comparat
  const valid = quotes.filter((q) => meta.has(q.rfqId) && q.price > 0);

  const groups = new Map<string, Map<string, PriceQuote[]>>();
  for (const q of valid) {
    const key = productKey(q.desc, q.unit);
    const byRfq = groups.get(key) ?? new Map<string, PriceQuote[]>();
    byRfq.set(q.rfqId, [...(byRfq.get(q.rfqId) ?? []), q]);
    groups.set(key, byRfq);
  }

  const products: MarketProduct[] = [];
  for (const [key, byRfq] of groups) {
    const order = [...byRfq.keys()]
      .map((id) => meta.get(id)!)
      .sort((a, b) => (a.at === b.at ? a.id.localeCompare(b.id) : a.at < b.at ? -1 : 1));
    const lastMeta = order[order.length - 1]!;
    const prevMeta = order.length > 1 ? order[order.length - 2]! : null;
    const inPeriod = order.filter((r) => from == null || r.at >= from);
    const period = inPeriod.length ? inPeriod : [lastMeta];

    const minOf = (id: string): number => Math.min(...byRfq.get(id)!.map((q) => q.price));

    // câștigurile: la egalitate, fiecare furnizor cu minimul primește câștigul
    const winCount = new Map<string, { supplier: SupplierRef; count: number }>();
    const points: MarketPoint[] = [];
    let lo = Infinity;
    let hi = -Infinity;
    for (const r of period) {
      const list = byRfq.get(r.id)!;
      const min = minOf(r.id);
      const best = [...perSupplier(list).values()]
        .filter((q) => q.price === min)
        .sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
      for (const q of best) {
        const w = winCount.get(q.supplier.id) ?? { supplier: q.supplier, count: 0 };
        w.count++;
        winCount.set(q.supplier.id, w);
      }
      points.push({ rfqId: r.id, at: r.at, price: min, supplier: best[0]!.supplier });
      for (const q of list) {
        lo = Math.min(lo, q.price);
        hi = Math.max(hi, q.price);
      }
    }
    const wins = [...winCount.values()].sort(
      (a, b) => b.count - a.count || byName(a.supplier, b.supplier),
    );
    const winsOf = (id: string): number => winCount.get(id)?.count ?? 0;

    const lastQuotes = byRfq.get(lastMeta.id)!;
    const now = minOf(lastMeta.id);
    // primul din clasament e recomandarea: preț, apoi câștiguri, apoi cine a răspuns primul
    const ranked = [...perSupplier(lastQuotes).values()].sort(
      (a, b) =>
        a.price - b.price ||
        winsOf(b.supplier.id) - winsOf(a.supplier.id) ||
        (a.at < b.at ? -1 : a.at > b.at ? 1 : 0),
    );
    const rank: MarketRank[] = ranked.map((q) => ({
      supplier: q.supplier,
      price: q.price,
      diff: q.price / now - 1,
      best: q.price === now,
    }));
    const nextRow = rank.find((r) => r.price > now);
    // ofertele primite la cerere, nu doar cele cu preț la acest material: un
    // furnizor care n-are Ø12 a răspuns totuși
    const received = Math.max(lastMeta.responded ?? 0, rank.length);
    const sample = [...lastQuotes].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))[0]!;

    products.push({
      key,
      desc: sample.desc,
      unit: sample.unit,
      requests: period.length,
      last: {
        rfqId: lastMeta.id,
        rfqTitle: lastMeta.title,
        at: lastMeta.at,
        received,
        invited: lastMeta.invited,
        complete: !lastMeta.open || received >= lastMeta.invited,
      },
      now,
      pick: rank[0]!.supplier,
      ties: rank
        .slice(1)
        .filter((r) => r.best)
        .map((r) => r.supplier),
      next: nextRow ? { supplier: nextRow.supplier, price: nextRow.price } : null,
      change: prevMeta ? now / minOf(prevMeta.id) - 1 : null,
      rank,
      points,
      lo,
      hi,
      wins,
      inPeriod: inPeriod.length > 0,
    });
  }

  products.sort((a, b) => (a.last.at < b.last.at ? 1 : a.last.at > b.last.at ? -1 : 0));

  // culorile: furnizorii cu care firma lucrează cel mai des, aceiași pe toată pagina
  const counts = new Map<string, { supplier: SupplierRef; n: number }>();
  for (const q of valid) {
    const c = counts.get(q.supplier.id) ?? { supplier: q.supplier, n: 0 };
    c.n++;
    counts.set(q.supplier.id, c);
  }
  const slots: Record<string, number> = {};
  [...counts.values()]
    .sort((a, b) => b.n - a.n || byName(a.supplier, b.supplier))
    .slice(0, MARKET_SLOTS)
    .forEach((c, i) => (slots[c.supplier.id] = i));

  return { products, slots };
}

/** Materialele din bandă: cu variație, cerute în perioadă, cele recente primele. */
export function bandOf(
  products: MarketProduct[],
  from: string | null,
  max = BAND_MAX,
): MarketProduct[] {
  return products
    .filter((p) => p.change != null && (from == null || p.last.at >= from))
    .sort((a, b) => (a.last.at < b.last.at ? 1 : a.last.at > b.last.at ? -1 : 0))
    .slice(0, max);
}
